import * as React from 'react';
import {Mutation} from 'react-apollo';
import {DragDropContext} from 'react-beautiful-dnd';
import type {DropResult} from 'react-beautiful-dnd';

import remove from 'lodash/remove';
import * as ramda from 'ramda';

import type {
    SalesAutomationStage as SalesAutomationStageFragment,
    SalesAutomationStepTemplate as SalesAutomationStepTemplateFragment,
    RepositionStepTemplate as RepositionStepTemplateMutation,
    RepositionStepTemplateVariables as RepositionStepTemplateMutationVariables,
    StepTemplatesForStageQuery as StepTemplatesForStageQueryQuery,
} from 'nutshell-graphql-types';

import {SAStepTemplatesFeatureGate} from '../../../limits/feature-gates/sales-automation-step-templates-feature-gate';

import {STEP_TEMPLATES} from '../../constants';
import {SAStepTemplatesCtaBanner} from '../../ctas/step-templates-cta-banner';
import SALES_AUTOMATION_STEP_TEMPLATE from '../../graphql/fragments/sales-automation-step-template.graphql';
import REPOSITION_STEP_TEMPLATE from '../../graphql/mutations/reposition-step-template.graphql';
import GET_STEP_TEMPLATES_FOR_STAGE from '../../graphql/queries/get-step-templates-for-stage.graphql';

import {calculateBoardWidth} from '../calculate-board-width';
import {StageCardsStepTemplatesContainer} from '../card-containers/stage-cards-step-templates-container';
import {refetchSalesAutomationOpenLeadQueries} from '../sales-automation-open-leads';

import {CollapsibleSection} from './collapsible-section';

type OwnProps = {
    stages: SalesAutomationStageFragment[];
    hideCards: boolean;
};

type GraphQLProps = {
    repositionStepTemplate: (arg1: any) => Promise<any>;
};

type Props = OwnProps & GraphQLProps;

type State = {
    isCardDragDisabled: boolean;
    isCardBeingDragged: boolean;
    isFeatureGateModalOpen: boolean;
};

/*
 * Top-level component responsible for rendering the entire "step templates"
 * section (row) on the sales automation setup board.
 */
export class SalesAutomationStepTemplatesComponent extends React.PureComponent<Props, State> {
    constructor() {
        // @ts-expect-error
        super();

        this.state = {
            isCardBeingDragged: false,
            isCardDragDisabled: false,
            isFeatureGateModalOpen: false,
        };
    }

    setIsFeatureGateModalOpen = (isOpen: boolean) => {
        this.setState({isFeatureGateModalOpen: isOpen});
    };

    render() {
        const collapsibleSectionContents = NutCapabilities.SAOpenLeadTasks.isAvailable ? (
            this.props.stages.map((stage) => {
                return (
                    <StageCardsStepTemplatesContainer
                        key={stage.id}
                        parentId={stage.id}
                        hideCards={this.props.hideCards}
                        isCardDragDisabled={this.state.isCardDragDisabled}
                        isCardBeingDragged={this.state.isCardBeingDragged}
                    />
                );
            })
        ) : (
            <SAStepTemplatesCtaBanner
                width={calculateBoardWidth(this.props.stages.length)}
                onClick={() => this.setIsFeatureGateModalOpen(true)}
            />
        );

        return (
            <>
                <DragDropContext
                    onDragStart={() => this.setState({isCardBeingDragged: true})}
                    onDragEnd={this.handleItemMoved}
                >
                    <CollapsibleSection type={STEP_TEMPLATES}>
                        {collapsibleSectionContents}
                    </CollapsibleSection>
                </DragDropContext>
                <SAStepTemplatesFeatureGate
                    isOpen={this.state.isFeatureGateModalOpen}
                    setIsOpen={this.setIsFeatureGateModalOpen}
                />
            </>
        );
    }

    handleItemMoved = (result: DropResult) => {
        this.setState({
            // Disable more card drags until the mutation finishes.
            isCardDragDisabled: true,
        });

        if (!result.destination) {
            this.setState({
                isCardDragDisabled: false,
                isCardBeingDragged: false,
            });

            return;
        }

        const originalIndex = result.source.index;
        const destinationIndex = result.destination.index;

        const destinationStage = result.destination.droppableId;
        const originalStage = result.source.droppableId;

        // If dropped in the original location, and in the same stage, do nothing
        if (originalIndex === destinationIndex && destinationStage === originalStage) {
            this.setState({
                isCardDragDisabled: false,
                isCardBeingDragged: false,
            });

            return;
        }

        this.props
            .repositionStepTemplate({
                stepTemplateId: result.draggableId,
                originalStageId: result.source.droppableId,
                destinationStageId: result.destination.droppableId,
                destinationPosition: result.destination.index,
            })
            .then(() => {
                this.setState({
                    isCardDragDisabled: false,
                    isCardBeingDragged: false,
                });
            });
    };
}

class RepositionMutation extends Mutation<
    RepositionStepTemplateMutation,
    RepositionStepTemplateMutationVariables
> {}

export const SalesAutomationStepTemplates = (props: OwnProps) => (
    <RepositionMutation
        mutation={REPOSITION_STEP_TEMPLATE}
        refetchQueries={(mutationResult) => {
            if (
                !mutationResult ||
                !mutationResult.data ||
                !mutationResult.data.repositionStepTemplate
            ) {
                return [];
            }

            const repositionStepTemplate = mutationResult.data.repositionStepTemplate;
            const oldStage = repositionStepTemplate.oldStage;
            const newStage = repositionStepTemplate.newStage;

            const refetchOfOldStage = refetchSalesAutomationOpenLeadQueries({stageId: oldStage.id});
            const refetchOfNewStage = refetchSalesAutomationOpenLeadQueries({stageId: newStage.id});

            // @ts-expect-error
            return ramda.uniq(refetchOfOldStage.concat(refetchOfNewStage));
        }}
        update={(proxy, mutationResult) => {
            if (
                !mutationResult ||
                !mutationResult.data ||
                !mutationResult.data.repositionStepTemplate
            )
                return;

            const repositionStepTemplate = mutationResult.data.repositionStepTemplate;
            const oldStage = repositionStepTemplate.oldStage;
            const newStage = repositionStepTemplate.newStage;

            proxy.writeQuery({
                query: GET_STEP_TEMPLATES_FOR_STAGE,
                variables: {stageId: oldStage.id},
                data: {
                    editableStepTemplates: oldStage.editableStepTemplates,
                },
            });

            proxy.writeQuery({
                query: GET_STEP_TEMPLATES_FOR_STAGE,
                variables: {stageId: newStage.id},
                data: {
                    editableStepTemplates: newStage.editableStepTemplates,
                },
            });
        }}
    >
        {(reposition, {client}) => (
            <SalesAutomationStepTemplatesComponent
                {...props}
                repositionStepTemplate={(repositionStepTemplatePayload) => {
                    const {
                        stepTemplateId,
                        originalStageId,
                        destinationStageId,
                        destinationPosition,
                    } = repositionStepTemplatePayload;

                    const stepTemplate: SalesAutomationStepTemplateFragment | null | undefined =
                        client.readFragment({
                            id: stepTemplateId,
                            fragmentName: 'SalesAutomationStepTemplate',
                            fragment: SALES_AUTOMATION_STEP_TEMPLATE,
                        });
                    const originalStageData: StepTemplatesForStageQueryQuery | null | undefined =
                        client.readQuery({
                            query: GET_STEP_TEMPLATES_FOR_STAGE,
                            variables: {
                                stageId: originalStageId,
                            },
                        });
                    const destinationStageData: StepTemplatesForStageQueryQuery | null | undefined =
                        client.readQuery({
                            query: GET_STEP_TEMPLATES_FOR_STAGE,
                            variables: {
                                stageId: destinationStageId,
                            },
                        });

                    if (!originalStageData || !destinationStageData) {
                        return Promise.reject(
                            `Could not find stage data in cache, ${JSON.stringify({
                                originalStageData,
                                destinationStageData,
                            })}`
                        );
                    }

                    if (!stepTemplate) {
                        return Promise.reject('Could not find step template fragment');
                    }

                    remove(originalStageData.editableStepTemplates, {
                        id: stepTemplateId,
                    });
                    remove(destinationStageData.editableStepTemplates, {
                        id: stepTemplateId,
                    });

                    destinationStageData.editableStepTemplates.splice(
                        destinationPosition,
                        0,
                        stepTemplate
                    );

                    return reposition({
                        variables: {
                            stepTemplatePositionPayload: {
                                stepTemplateId: repositionStepTemplatePayload.stepTemplateId,
                                destinationStageId:
                                    repositionStepTemplatePayload.destinationStageId,
                                destinationPosition:
                                    repositionStepTemplatePayload.destinationPosition,
                            },
                        },
                        optimisticResponse: {
                            // @ts-expect-error
                            __typename: 'Mutation',
                            repositionStepTemplate: {
                                stepTemplate: stepTemplate,
                                newStage: {
                                    __typename: 'Stage',
                                    id: repositionStepTemplatePayload.destinationStageId,
                                    editableStepTemplates:
                                        destinationStageData.editableStepTemplates,
                                },
                                oldStage: {
                                    __typename: 'Stage',
                                    id: repositionStepTemplatePayload.originalStageId,
                                    editableStepTemplates: originalStageData.editableStepTemplates,
                                },
                                __typename: 'RepositionStepTemplatePayload',
                            },
                        },
                    });
                }}
            />
        )}
    </RepositionMutation>
);
